// src/components/product/BundleEditor/BundleBulkPanel.jsx
import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import Dropdown from '../../ui/Dropdown/Dropdown';
import BundleCheckbox from './BundleCheckbox';
import styles from './BundleEditor.module.css';

const ACTIONS = [
  { value: 'setValue', labelKey: 'products.bulkSetValue', needsValue: true },
  { value: 'setDiscountAmount', labelKey: 'products.bulkSetDiscountAmount', needsValue: true },
  { value: 'setDiscountPercent', labelKey: 'products.bulkSetDiscountPercent', needsValue: true },
  { value: 'makeFree', labelKey: 'products.bulkMakeFree' },
  { value: 'fillFromShopPrice', labelKey: 'products.bulkFillFromShopPrice' },
  { value: 'copyShop', labelKey: 'products.bulkCopyShop', needsSource: true },
];

const BundleBulkPanel = ({ selectedCount, shops, bundleShopIds, onApply, onCancel }) => {
  const { t } = useLocalization();
  const enabledShops = shops.filter(s => bundleShopIds.some(id => String(id) === String(s.id)));
  const [shopIds, setShopIds] = useState(enabledShops.map(s => s.id));
  const [action, setAction] = useState('setValue');
  const [value, setValue] = useState('');
  const [sourceShopId, setSourceShopId] = useState('');

  const current = ACTIONS.find(a => a.value === action);
  const canApply = shopIds.length > 0
    && (!current.needsValue || value !== '')
    && (!current.needsSource || sourceShopId !== '');

  const toggleShop = (id) => setShopIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  return (
    <div className={styles.bulkActionsPanel}>
      <div className={styles.bulkActionsHeader}>
        <h4>{t('products.bulkActions')}</h4>
        <p>{t('products.selectedCount', { count: selectedCount })}</p>
      </div>
      <div className={styles.bulkActionsContent}>
        <div className={styles.bulkShopGrid}>
          {enabledShops.map(shop => (
            <BundleCheckbox
              key={shop.id}
              checked={shopIds.includes(shop.id)}
              onChange={() => toggleShop(shop.id)}
              className={`${styles.bulkShopCard} ${styles.shopCheckbox}`}
              label={(
                <>
                  <Building2 size={14} className={styles.shopIcon} />
                  <span className={styles.shopName}>{shop.name}</span>
                </>
              )}
            />
          ))}
        </div>

        <div className={styles.bulkControls}>
          <Dropdown
            value={action}
            onChange={v => v && setAction(v)}
            options={ACTIONS.map(a => ({ value: a.value, label: t(a.labelKey) }))}
            allowClear={false}
            className={styles.bulkDropdown}
          />
          {current.needsValue && (
            <input type="number" min="0" step="0.01" value={value} onChange={e => setValue(e.target.value)} />
          )}
          {current.needsSource && (
            <Dropdown
              value={sourceShopId}
              onChange={v => setSourceShopId(v || '')}
              options={enabledShops.map(s => ({ value: s.id, label: s.name }))}
              placeholder={t('products.copyFromShop')}
              allowClear={false}
              className={styles.bulkDropdown}
            />
          )}
        </div>
      </div>
      <div className={styles.bulkActionsFooter}>
        <button type="button" className={styles.cancelBulkButton} onClick={onCancel}>{t('common.cancel')}</button>
        <button
          type="button"
          className={styles.applyBulkButton}
          disabled={!canApply}
          onClick={() => onApply({ shopIds, action, value, sourceShopId })}
        >
          {t('products.applyBulk')}
        </button>
      </div>
    </div>
  );
};

export default BundleBulkPanel;
